import type {
  SmartCreationAdGroupDraftContentV2,
  SmartCreationDraftContentV2,
  SmartCreationStrategyTargetIntent,
} from "../schemas/smart-creation";
import type { SmartCreationReviewTarget } from "../schemas/smart-creation-review";
import type { AddedReviewTarget } from "./types";

export function reviewTargetIntent(
  target: SmartCreationReviewTarget | AddedReviewTarget,
): SmartCreationStrategyTargetIntent {
  switch (target.type) {
    case "keyword": {
      const matchType =
        typeof target.matchType === "string" ? target.matchType : target.matchType.effective;
      return { type: "keyword", keyword: target.keyword, matchType };
    }
    case "product":
      return {
        type: "product",
        asin: target.asin.trim().toUpperCase(),
        matchType: target.matchType ?? "exact",
      };
    case "automatic":
      return { type: "auto", expressionType: target.expressionType };
    case "category":
      return { type: "category", categoryId: target.categoryId.trim() };
    case "category_brand":
      return {
        type: "category_brand",
        categoryId: target.categoryId.trim(),
        brandId: target.brandId.trim(),
      };
  }
}

export function strategySupportsReviewIntent(
  kind: SmartCreationReviewTarget["strategyKind"],
  intent: SmartCreationStrategyTargetIntent,
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
): boolean {
  // Review edits obey the same Strategy contract as Step 3, without rewriting legacy snapshots.
  if (previewGeneratorVersion < 3) return true;
  if (intent.type === "product") {
    return kind === "research" || (kind !== "automatic" && intent.matchType !== "expanded");
  }
  if (intent.type === "keyword") {
    return kind === "research"
      ? intent.matchType !== "exact"
      : kind === "performance"
        ? intent.matchType === "exact"
        : kind === "branded" || kind === "competitor";
  }
  if (intent.type === "category_brand") return kind === "competitor" || kind === "branded";
  return intent.type === "category" ? kind === "research" : kind === "automatic";
}

export function strategyOwnsAdGroup(
  adGroup: SmartCreationAdGroupDraftContentV2,
  strategyKind: SmartCreationReviewTarget["strategyKind"],
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
): boolean {
  return previewGeneratorVersion < 3 || adGroup.targeting.targets[0]?.strategyKind === strategyKind;
}
